import MasterCrudPage from "../../components/MasterCrudPage";
import {
  assetTypeApi,
  fetchLinkedAssets,
  serviceApi,
  serviceCategoryApi,
  serviceSubCategoryApi,
  syncLinkedAssets,
} from "../../api/masterApi";

export default function ServiceMaster() {
  return (
    <MasterCrudPage
      title="Asset Master"
      description="Add assets, variants, pricing, media and linked assets."
      idKey="service_id"
      api={serviceApi}
      listLabel="assets"
      loadExtraOptions={async () => {
        const [assetTypes, categories, subCategories, assets] = await Promise.all([
          assetTypeApi.list(),
          serviceCategoryApi.list(),
          serviceSubCategoryApi.list(),
          serviceApi.list(),
        ]);
        return {
          assetTypes: assetTypes.map((type) => ({
            value: type.asset_type_id,
            label: type.asset_type_name,
          })),
          serviceCategories: categories.map((c) => ({
            value: c.service_category_id,
            label: c.service_category_name,
          })),
          serviceSubCategories: subCategories.map((c) => ({
            value: c.service_sub_category_id,
            label: c.service_sub_category_name,
            parentId: c.service_category_id,
          })),
          assets: assets.map((asset) => ({
            value: asset.service_id,
            label: asset.service_title,
          })),
          assetNames: assets.map((asset) => ({
            value: asset.service_id,
            label: asset.service_title,
          })),
        };
      }}
      loadForEdit={async (item) => {
        const linked = await fetchLinkedAssets(item.service_id);
        return {
          linked_asset_ids: linked.map((asset) => String(asset.linked_service_id)),
        };
      }}
      onAfterSave={async (saved, form) => {
        await syncLinkedAssets(
          saved.service_id,
          (form.linked_asset_ids || []).map(Number).filter(Boolean)
        );
      }}
      fields={[
        {
          name: "asset_type_id",
          label: "Asset Type",
          type: "select",
          optionsKey: "assetTypes",
          placeholder: "Select Asset Type",
        },
        {
          name: "parent_asset_id",
          label: "Parent Asset",
          type: "select",
          optionsKey: "assets",
          placeholder: "None (Top-level asset)",
        },
        {
          name: "is_variant",
          label: "Is Variant",
          type: "radio",
          defaultValue: "0",
          options: [
            { value: "1", label: "Yes" },
            { value: "0", label: "No" },
          ],
        },
        {
          name: "service_category_id",
          label: "Category",
          type: "select",
          required: true,
          optionsKey: "serviceCategories",
          placeholder: "Select Category",
        },
        {
          name: "service_sub_category_id",
          label: "Sub Category",
          type: "select",
          required: true,
          optionsKey: "serviceSubCategories",
          filterBy: "service_category_id",
          placeholder: "Select Sub Category",
        },
        {
          name: "service_title",
          label: "Asset Title",
          type: "text",
          required: true,
          placeholder: "e.g. Vastu Consultation",
        },
        { name: "service_icon", label: "Asset Icon", type: "image" },
        {
          name: "asset_profile_picture",
          label: "Asset Profile Picture",
          type: "image",
        },
        {
          name: "asset_picture_gallery",
          label: "Asset Picture Gallery",
          type: "files",
        },
        {
          name: "asset_videos",
          label: "Asset Video",
          type: "videos",
        },
        {
          name: "short_description",
          label: "Short Description",
          type: "textarea",
          fullWidth: true,
        },
        {
          name: "long_description",
          label: "Long Description",
          type: "richtext",
          fullWidth: true,
        },
        {
          name: "price_inr",
          label: "Asset Price in INR",
          type: "number",
          min: 0,
          step: "0.01",
        },
        {
          name: "price_usd",
          label: "Asset Price in USD",
          type: "number",
          min: 0,
          step: "0.01",
        },
        {
          name: "gst_percent",
          label: "GST%",
          type: "number",
          min: 0,
          step: "0.01",
        },
        {
          name: "sort_order",
          label: "Sort Order",
          type: "number",
          min: 0,
          step: "1",
          defaultValue: "0",
        },
        {
          name: "linked_asset_ids",
          label: "Linked Asset",
          type: "multiselect",
          optionsKey: "assetNames",
          fullWidth: true,
        },
        { name: "status", label: "Status", type: "checkbox" },
      ]}
      columns={[
        { key: "service_id", label: "Asset" },
        { key: "asset_type_name", label: "Asset Type" },
        { key: "parent_asset_title", label: "Parent Asset" },
        {
          key: "is_variant",
          label: "Variant",
          render: (item) => (Number(item.is_variant) === 1 ? "Yes" : "No"),
        },
        { key: "service_category_name", label: "Category" },
        { key: "service_sub_category_name", label: "Sub Category" },
        { key: "service_title", label: "Asset Title" },
        { key: "service_icon", label: "Asset Icon", type: "image" },
        { key: "asset_profile_picture", label: "Profile Picture", type: "image" },
        { key: "price_inr", label: "Price INR" },
        { key: "price_usd", label: "Price USD" },
        { key: "gst_percent", label: "GST%" },
        { key: "sort_order", label: "Sort Order" },
        { key: "short_description", label: "Short Description" },
        { key: "status", label: "Status", type: "status" },
      ]}
    />
  );
}
